import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TelemetryAPI } from '../../services/telemetry/telemetryApi';
import { TraceResponse } from '../../types/telemetry';
import {
    MagnifyingGlassIcon,
    ChevronRightIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    ClipboardDocumentIcon,
    HandRaisedIcon,
} from '@heroicons/react/24/outline';

const buildSpanTree = (spans: any[]) => {
    const spanMap = new Map(spans.map(span => [span.span_id, { ...span, children: [] }]));
    for (const span of spanMap.values()) {
        if (span.parent_span_id) {
            const parentSpan = spanMap.get(span.parent_span_id);
            if (parentSpan) parentSpan.children.push(span);
        }
    }
    return Array.from(spanMap.values()).filter(span => !span.parent_span_id);
};

const SpanNode: React.FC<{ span: any; depth?: number; onSpanSelect: (span: any) => void }>
    = ({ span, depth = 0, onSpanSelect }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const statusColor = span.status === 'error' ? 'border-danger-500 bg-gradient-to-r from-danger-50/20 to-danger-100/20 dark:from-danger-900/10 dark:to-danger-800/10' : 'border-success-500 bg-gradient-to-r from-success-50/20 to-success-100/20 dark:from-success-900/10 dark:to-success-800/10';
        return (
            <div className={`p-3 mb-3 rounded-r-xl border-l-4 shadow-lg backdrop-blur-xl glass ${statusColor}`} style={{ marginLeft: `${depth * 20}px` }}>
                <div
                    className="flex justify-between items-center p-2 rounded-lg transition-all duration-200 cursor-pointer hover:bg-gradient-primary/5"
                    onClick={() => { onSpanSelect(span); setIsExpanded(!isExpanded); }}
                >
                    <div className="flex gap-2 items-center">
                        {span.children.length > 0 && (
                            <div className="flex justify-center items-center w-6 h-6 rounded-lg bg-gradient-primary/20">
                                <ChevronRightIcon className={`w-3 h-3 text-primary-600 dark:text-primary-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </div>
                        )}
                        <span className="font-medium font-display text-secondary-900 dark:text-white">{span.operation_name}</span>
                        {span.status === 'error' && (
                            <div className="flex justify-center items-center w-5 h-5 rounded-full shadow-lg bg-gradient-danger">
                                <ExclamationTriangleIcon className="w-3 h-3 text-white" />
                            </div>
                        )}
                    </div>
                    <span className="font-semibold font-display gradient-text-accent">{Number(span.duration_ms || 0).toFixed(2)} ms</span>
                </div>
                {isExpanded && span.children.map((child: any) => (
                    <SpanNode key={child.span_id} span={child} depth={depth + 1} onSpanSelect={onSpanSelect} />
                ))}
            </div>
        );
    };

export const TraceViewer: React.FC = () => {
    const [traceId, setTraceId] = useState('');
    const [selectedSpan, setSelectedSpan] = useState<any>(null);

    const { data: traceData, isLoading, error, refetch, isFetching } = useQuery<TraceResponse>({
        queryKey: ['telemetry-trace', traceId],
        queryFn: () => (traceId ? TelemetryAPI.getTraceDetails(traceId) : Promise.reject(new Error('No trace ID'))),
        enabled: !!traceId,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    });

    const spanTree = useMemo(() => (traceData ? buildSpanTree(traceData.spans || []) : []), [traceData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (traceId) refetch();
    };

    const renderErrorMessage = (): React.ReactNode => {
        const errorMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : 'An unknown error occurred';
        return (
            <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-r rounded-xl border shadow-lg backdrop-blur-xl glass border-danger-200/30 dark:border-danger-500/20 from-danger-50/30 to-danger-100/30 dark:from-danger-900/20 dark:to-danger-800/20">
                <div className="flex gap-2 sm:gap-3 items-center">
                    <div className="flex justify-center items-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg shadow-lg bg-gradient-danger shrink-0">
                        <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <span className="text-xs sm:text-sm md:text-base font-body text-secondary-900 dark:text-white">
                        Error loading trace details: {errorMessage}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl border shadow-xl backdrop-blur-xl glass border-primary-200/30 bg-gradient-light-panel dark:bg-gradient-dark-panel">
            <div className="mb-4 sm:mb-5 md:mb-6">
                <div className="flex gap-2 sm:gap-3 items-center mb-3 sm:mb-4">
                    <div className="flex justify-center items-center w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-xl shadow-lg bg-gradient-highlight glow-highlight shrink-0">
                        <MagnifyingGlassIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold font-display gradient-text-primary">Trace Viewer</h2>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
                    <input
                        type="text"
                        value={traceId}
                        onChange={(e) => setTraceId(e.target.value)}
                        placeholder="Enter Trace ID"
                        className="flex-grow input text-xs sm:text-sm"
                    />
                    <button
                        type="submit"
                        className="btn inline-flex gap-1.5 sm:gap-2 items-center justify-center px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-semibold text-white rounded-xl border shadow-lg backdrop-blur-xl transition-all duration-200 btn btn-primary glass border-primary-200/30 bg-gradient-primary hover:bg-gradient-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-display"
                        disabled={!traceId || isFetching}
                    >
                        <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        {isFetching ? 'Loading...' : 'View Trace'}
                    </button>
                </form>
            </div>

            {isLoading && (
                <div className="space-y-4 animate-pulse">
                    {[...Array(5)].map((_, i) => (<div key={i} className="h-12 rounded-xl bg-gradient-primary/20" />))}
                </div>
            )}

            {!isLoading && error ? renderErrorMessage() : null}

            {traceData && (
                <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-5 lg:gap-6 md:grid-cols-3">
                    <div className="md:col-span-2">
                        <div className="flex gap-2 sm:gap-3 items-center mb-3 sm:mb-4">
                            <div className="flex justify-center items-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg shadow-lg bg-gradient-secondary glow-secondary shrink-0">
                                <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <h3 className="text-sm sm:text-base font-semibold font-display gradient-text-secondary">Trace Spans (Total: {traceData.summary?.total_spans || 0})</h3>
                        </div>
                        <div className="max-h-[300px] sm:max-h-[400px] md:max-h-[500px] overflow-y-auto glass rounded-xl p-3 sm:p-4 border border-primary-200/30 dark:border-primary-500/20 shadow-lg backdrop-blur-xl">
                            {spanTree.length === 0 ? (
                                <div className="py-6 sm:py-8 text-center">
                                    <div className="flex justify-center items-center mx-auto mb-2 sm:mb-3 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-accent/20">
                                        <MagnifyingGlassIcon className="w-5 h-5 sm:w-6 sm:h-6 text-accent-500 dark:text-accent-400" />
                                    </div>
                                    <p className="text-xs sm:text-sm font-body text-secondary-600 dark:text-secondary-300">No spans found for this trace</p>
                                </div>
                            ) : (
                                spanTree.map((span) => (<SpanNode key={span.span_id} span={span} onSpanSelect={setSelectedSpan} />))
                            )}
                        </div>
                    </div>

                    <div className="p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl border shadow-lg backdrop-blur-xl glass border-highlight-200/30 dark:border-highlight-500/20 bg-gradient-light-panel dark:bg-gradient-dark-panel">
                        <div className="flex gap-2 sm:gap-3 items-center mb-3 sm:mb-4">
                            <div className="flex justify-center items-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg shadow-lg bg-gradient-highlight glow-highlight shrink-0">
                                <ClipboardDocumentIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <h3 className="text-sm sm:text-base font-semibold font-display gradient-text-highlight">Span Details</h3>
                        </div>
                        {selectedSpan ? (
                            <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                                <div className="p-2.5 sm:p-3 rounded-lg border shadow-lg backdrop-blur-xl glass border-primary-200/30 dark:border-primary-500/20">
                                    <span className="text-xs sm:text-sm font-medium font-display gradient-text-primary">Operation:</span>
                                    <div className="mt-1 text-xs sm:text-sm font-body text-secondary-900 dark:text-white break-words">{selectedSpan.operation_name}</div>
                                </div>
                                <div className="p-2.5 sm:p-3 rounded-lg border shadow-lg backdrop-blur-xl glass border-accent-200/30 dark:border-accent-500/20">
                                    <span className="text-xs sm:text-sm font-medium font-display gradient-text-accent">Duration:</span>
                                    <div className="mt-1 text-xs sm:text-sm font-semibold font-display gradient-text-accent">{Number(selectedSpan.duration_ms || 0).toFixed(2)} ms</div>
                                </div>
                                <div className="p-2.5 sm:p-3 rounded-lg border shadow-lg backdrop-blur-xl glass border-secondary-200/30 dark:border-secondary-500/20">
                                    <span className="text-xs sm:text-sm font-medium font-display gradient-text-secondary">Status:</span>
                                    <span className={`ml-2 glass px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-display font-semibold border shadow-lg backdrop-blur-xl ${selectedSpan.status === 'error' ? 'border-danger-200/30 bg-gradient-to-r from-danger-50/30 to-danger-100/30 dark:from-danger-900/20 dark:to-danger-800/20 text-danger-700 dark:text-danger-300' : 'border-success-200/30 bg-gradient-to-r from-success-50/30 to-success-100/30 dark:from-success-900/20 dark:to-success-800/20 text-success-700 dark:text-success-300'}`}>{selectedSpan.status}</span>
                                </div>
                                <div className="p-2.5 sm:p-3 rounded-lg border shadow-lg backdrop-blur-xl glass border-highlight-200/30 dark:border-highlight-500/20">
                                    <span className="block mb-1.5 sm:mb-2 text-xs sm:text-sm font-medium font-display gradient-text-highlight">Attributes:</span>
                                    <pre className="overflow-x-auto p-2 sm:p-3 font-mono text-[10px] sm:text-xs rounded border shadow-lg backdrop-blur-xl glass border-primary-200/30 dark:border-primary-500/20 text-secondary-900 dark:text-white">{JSON.stringify(selectedSpan.attributes || {}, null, 2)}</pre>
                                </div>
                            </div>
                        ) : (
                            <div className="py-6 sm:py-8 text-center">
                                <div className="flex justify-center items-center mx-auto mb-2 sm:mb-3 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-info/20">
                                    <HandRaisedIcon className="w-5 h-5 sm:w-6 sm:h-6 text-info-500 dark:text-info-400" />
                                </div>
                                <p className="text-xs sm:text-sm font-body text-secondary-600 dark:text-secondary-300">Select a span to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
